/**
 * Super Admin - CB Accreditation Detail & Decision
 * View details and make decisions on CB accreditation
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCBAccreditation, useMakeCBDecision } from '../hooks/useAccreditation';
import {
  AccreditationStatusBadge,
  AccreditationTimeline,
  AccreditationDecisionForm,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage
} from '../components/AccreditationComponents';
import { formatDate } from '../api/accreditationService';

const CBAccreditationDetail = () => {
  const { accreditationId } = useParams();
  const navigate = useNavigate();
  const { accreditation, loading, error, refetch } = useCBAccreditation(accreditationId);
  const { makeDecision, loading: decisionLoading, error: decisionError } = useMakeCBDecision();
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleDecision = async (decisionData) => {
    try {
      await makeDecision(
        accreditationId,
        decisionData.newStatus,
        decisionData.decisionNotes,
        decisionData.reason
      );
      setSuccessMessage('Decision submitted successfully!');
      setShowDecisionForm(false);
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      // Error handled by hook
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '32px'
  };

  const backButtonStyle = {
    padding: '8px 16px',
    background: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '16px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '8px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px'
  };

  const sectionStyle = {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    marginBottom: '24px'
  };

  const sectionTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px'
  };

  const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  };

  const infoItemStyle = {
    marginBottom: '12px'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#999',
    marginBottom: '4px'
  };

  const valueStyle = {
    fontSize: '1rem',
    color: '#333'
  };

  const scopeBadgeStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    background: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: '600',
    marginRight: '8px',
    marginBottom: '8px'
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#fff',
    background: '#3498db',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '16px'
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!accreditation) return <ErrorMessage message="Accreditation not found" />;

  return (
    <div style={containerStyle}>
      <button style={backButtonStyle} onClick={() => navigate('/accreditation/cert-bodies')}>
        ← Back to List
      </button>

      <div style={headerStyle}>
        <h1 style={titleStyle}>{accreditation.accreditation_number}</h1>
        <AccreditationStatusBadge status={accreditation.status} />
      </div>

      <SuccessMessage message={successMessage} />
      <ErrorMessage message={decisionError} />

      <div style={gridStyle}>
        <div>
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Accreditation Details</h2>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <div style={labelStyle}>Company ID</div>
                <div style={valueStyle}>{accreditation.company_id}</div>
              </div>

              <div style={infoItemStyle}>
                <div style={labelStyle}>Application Date</div>
                <div style={valueStyle}>{formatDate(accreditation.application_date)}</div>
              </div>

              {accreditation.granted_date && (
                <>
                  <div style={infoItemStyle}>
                    <div style={labelStyle}>Granted Date</div>
                    <div style={valueStyle}>{formatDate(accreditation.granted_date)}</div>
                  </div>

                  <div style={infoItemStyle}>
                    <div style={labelStyle}>Expiry Date</div>
                    <div style={valueStyle}>{formatDate(accreditation.expiry_date)}</div>
                  </div>
                </>
              )}

              {accreditation.next_surveillance_due && (
                <div style={infoItemStyle}>
                  <div style={labelStyle}>Next Surveillance</div>
                  <div style={valueStyle}>{formatDate(accreditation.next_surveillance_due)}</div>
                </div>
              )}

              {accreditation.next_recertification_due && (
                <div style={infoItemStyle}>
                  <div style={labelStyle}>Recertification Due</div>
                  <div style={valueStyle}>{formatDate(accreditation.next_recertification_due)}</div>
                </div>
              )}
            </div>

            {accreditation.decision_notes && (
              <div style={{ marginTop: '16px' }}>
                <div style={labelStyle}>Decision Notes</div>
                <div style={{ ...valueStyle, padding: '12px', background: '#f9f9f9', borderRadius: '4px', marginTop: '8px' }}>
                  {accreditation.decision_notes}
                </div>
              </div>
            )}

            {(accreditation.suspension_reason || accreditation.withdrawal_reason) && (
              <div style={{ marginTop: '16px' }}>
                <div style={labelStyle}>Suspension/Withdrawal Reason</div>
                <div style={{ ...valueStyle, padding: '12px', background: '#ffebee', borderRadius: '4px', marginTop: '8px', color: '#c62828' }}>
                  {accreditation.suspension_reason || accreditation.withdrawal_reason}
                </div>
              </div>
            )}
          </div>

          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Accreditation Scopes</h2>
            {accreditation.scopes && accreditation.scopes.length > 0 ? (
              <div>
                {accreditation.scopes.map((scope, index) => (
                  <div key={index}>
                    <span style={scopeBadgeStyle}>
                      {scope.scope?.scope_code || `Scope ${scope.scope_id}`}
                    </span>
                    {scope.scope?.scope_name && (
                      <span style={{ color: '#666', fontSize: '0.875rem' }}>
                        {scope.scope.scope_name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666' }}>No scopes defined</div>
            )}
          </div>

          {!showDecisionForm && accreditation.status !== 'granted' && (
            <button
              style={buttonStyle}
              onClick={() => setShowDecisionForm(true)}
            >
              Make Decision
            </button>
          )}

          {showDecisionForm && (
            <div style={sectionStyle}>
              <AccreditationDecisionForm
                onSubmit={handleDecision}
                loading={decisionLoading}
              />
              <button
                style={{ ...buttonStyle, background: '#95a5a6', marginTop: '12px' }}
                onClick={() => setShowDecisionForm(false)}
                disabled={decisionLoading}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div>
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Timeline</h2>
            <AccreditationTimeline accreditation={accreditation} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CBAccreditationDetail;
